import * as React from "react"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { PickerInline, PickerInlineProps } from "../picker/picker-inline"
import _ from "lodash"
import Model from "../../database/Model"
import SyncableModel from "../../database/SyncableModel"
import { Dimensions, Platform } from "react-native"
import { Spinner } from "native-base"
import { PickerActionsheetProps, PickerFlatListProps } from "../picker-actionsheet/picker-actionsheet"
import { FlatListProps } from "../flat-list/flat-list"
import { useSafeAreaInsets } from "react-native-safe-area-context"

export interface ModelPickerInlineProps extends Omit<PickerInlineProps, "fetchData"> {
  Model: typeof Model | typeof SyncableModel,
  fetchData?: (model: typeof Model | typeof SyncableModel, search?: string, params?: any) => Promise<Array<any>>,
  sortBy?: string
}

const defaultData = []
const defaultPickerActionsheetProps: Omit<PickerActionsheetProps, "renderItem"> = {}
const defaultFlatListProps: PickerFlatListProps<FlatListProps<any>> = {}

/**
 * Describe your component here
 */
export const ModelPickerInline = memo(function ModelPicker(props: ModelPickerInlineProps) {
  const {
    value, Model, dataKey = 'id', dataLabel = 'name', sortBy,
    data = defaultData, fetchData: fetchDataProp,
    pickerActionsheetProps: {
      inputProps,
      actionsheetStyle,
      flatListProps: { estimatedItemSize = 44, ...flatListProps } = defaultFlatListProps,
      ...pickerActionsheetProps
    } = defaultPickerActionsheetProps,
    InputRightElement,
    ...rest
  } = props

  const [ models, setModels ] = useState<Array<any>>(data || [])
  const [ fetchingData, setFetchingData ] = useState<boolean>(false)
  const insets = useSafeAreaInsets()

  const queryPromise = useRef<Promise<any>>(Promise.resolve())
  const mounted = useRef<boolean>(false)
  const previousSearch = useRef<string>(null)
  const previousValue = useRef<string>(null)

  const fetchData = useCallback(async (Model, search, params = {}) => {
    if (mounted.current) setFetchingData(true);
    const models = await (fetchDataProp && fetchDataProp(Model, search, params) || Model.fetch({
      search: search || undefined,
      sort: sortBy || dataLabel || undefined,
      ...params
    }))
    if (mounted.current) setFetchingData(false)
    return models;
  }, [dataLabel, fetchDataProp, sortBy])

  const queryModels = useMemo(() => _.debounce((search: string) => {
    queryPromise.current = queryPromise.current.then(() => {
      if (previousSearch.current === search) return undefined;
      previousSearch.current = search;
      return fetchData(Model, search)
        .catch((error) => {
          console.warn(error)
          return []
        })
        .then((models) => {
          if (mounted.current) setModels(models)
        })
    })
  }, 333), [Model, fetchData])

  useEffect(() => {
    mounted.current = true;
    queryModels('')

    return () => {
      mounted.current = false;
    }
  }, [queryModels])

  const initializeSelectedDatum = useCallback((callback) => {
    if (value) {
      if (previousValue.current === value) return;
      previousValue.current = value;
      fetchData(Model, '', { [`${dataKey}__in`]: value })
        .then((models) => models.find((m) => String(m[dataKey]) === String(value)))
        .then((model: Model) => callback(model))
    } else {
      callback(undefined)
    }
  }, [Model, dataKey, fetchData, value])

  const keyExtractor = useCallback((item: any, index: number) => (
    item && item[dataKey] || 'null'
  ), [dataKey])

  const minHeightMax = Dimensions.get("window").height * (Platform.OS === 'web' ? 0.5 : 0.8);
  const minHeightMin = Dimensions.get("window").height * 0.3;
  const estimatedHeight = (estimatedItemSize * models.length)
    + 24 + 32
    + (props.isUseSearch && 40 || 0) + 7
    + (pickerActionsheetProps?.onPressBottomButton && 60 || 0)
    + insets.bottom;
  let minHeight = minHeightMax;
  if (estimatedHeight < minHeightMin)
    minHeight = minHeightMin;
  else if (estimatedHeight < minHeightMax)
    minHeight = estimatedHeight;

  return (
    <PickerInline
      value={value}
      data={models}
      pickerActionsheetProps={{
        inputProps: {
          onChangeText: queryModels,
          InputRightElement: fetchingData ? <Spinner mr="9px"></Spinner> : undefined,
          ...inputProps
        },
        actionsheetStyle: {
          minHeight,
          ...(Platform.OS === 'web' ? { maxHeight: Dimensions.get("window").height * 0.5 } : {}),
          ...actionsheetStyle,
        },
        flatListProps: {
          estimatedItemSize,
          keyExtractor,
          ...flatListProps
        },
        loading: fetchingData,
        ...pickerActionsheetProps
      }}
      dataKey={dataKey}
      dataLabel={dataLabel}
      initializeSelectedDatum={initializeSelectedDatum}
      InputRightElement={InputRightElement || (fetchingData ? <Spinner mr="9px"></Spinner> : undefined)}
      {...rest}
    ></PickerInline>
  )
})
